/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Assignment settings form styles
 *
 * @author Satyaki Bose
 * @file assignmentSettingsFormStyles.js
 */

import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import mixins from '../../../../assets/styles/mixins.scss';
import {
  createPortalSlotStyles,
  createScopedClasses,
  createScopedStyles
} from '../../../../assets/styles/createScopedStyles';

const createInlineNumberFieldStyles = (height) => ({
  width: 56,
  flexShrink: 0,

  '& .MuiOutlinedInput-root': {
    borderRadius: 4,
    height,
    width: 56,
    boxSizing: 'border-box'
  },
  '& .MuiOutlinedInput-input': {
    textAlign: 'center',
    padding: '0 4px',
    height: '100%',
    boxSizing: 'border-box',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    color: mixins.timeIconColor
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: mixins.amethyst
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: mixins.amethyst
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: mixins.amethyst,
    borderWidth: 1
  },
  '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
    borderColor: mixins.amethyst
  }
});

const inlineNumberFieldDisabledStyles = {
  '& .MuiOutlinedInput-input.Mui-disabled': {
    WebkitTextFillColor: mixins.greyScaleColor,
    color: mixins.greyScaleColor
  }
};

const assignmentSettingsFormStyles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%',
    minWidth: 0
  },
  alertBanner: {
    borderRadius: 8
  },
  formPane: {
    backgroundColor: mixins.chalkWhiteFont,
    borderRadius: 16,
    padding: 24,
    boxSizing: 'border-box',
    minWidth: 0,
    width: '100%'
  },
  formBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%'
  },
  sectionGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    width: '100%'
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    minHeight: 40
  },
  sectionTitleGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 4
  },
  sectionTitle: {
    margin: 0,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 20,
    fontWeight: mixins.fontWeight500,
    lineHeight: '28px',
    letterSpacing: '-0.4px',
    color: mixins.color_text_primary
  },
  infoButton: {
    borderRadius: 1000,
    padding: 8,
    minWidth: 'auto',
    color: mixins.lightTextColor2,

    '& svg': {
      fontSize: mixins.fontSize18,
      width: mixins.fontSize18,
      height: mixins.fontSize18
    },

    '&:hover': {
      backgroundColor: mixins.lightThemeBgColor2
    }
  },
  divider: {
    height: 1,
    width: '100%',
    backgroundColor: mixins.color_elevation_alpha_12,
    border: 'none',
    margin: 0
  },
  radioCardsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 12,
    maxWidth: 920,
    width: '100%',

    [mixins['md-sm-maxwidth']]: {
      gridTemplateColumns: '1fr'
    }
  },
  dueDateRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'flex-end',
    maxWidth: 448
  },
  dueDateField: {
    minWidth: 220,
    flex: '1 1 220px'
  },
  equalWeightingBaseValueField: createInlineNumberFieldStyles(39),
  equalWeightingBaseValueFieldDisabled: inlineNumberFieldDisabledStyles,
  lateWorkPercentField: createInlineNumberFieldStyles(48),
  lateWorkPercentFieldDisabled: inlineNumberFieldDisabledStyles,
  dropdownField: {
    minWidth: 220,
    maxWidth: 296,
    width: '100%'
  },
  lateWorkContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%'
  },
  allowLateWorkRow: {
    padding: '0 8px'
  },
  allowLateWorkControl: {
    margin: 0,
    padding: 0
  },
  allowLateWorkCheckbox: {
    padding: 0,
    marginRight: 12,
    color: mixins.amethyst,
    '&.Mui-checked': {
      color: mixins.amethyst
    },
    '& svg': {
      fontSize: 18
    },
    '&.Mui-disabled': {
      '& $allowLateWorkCheckboxIcon': {
        fill: mixins.bulkEditCtaButtonDisabledBGColor,
        '& rect': {
          fill: mixins.bulkEditCtaButtonDisabledBGColor
        }
      }
    }
  },
  allowLateWorkCheckboxIcon: {},
  allowLateWorkLabel: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px',
    color: mixins.color_text_primary
  },
  lateWorkRuleRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    width: '100%'
  },
  lateWorkRuleRowDisabled: {
    opacity: 0.5,
    pointerEvents: 'none'
  },
  lateWorkText: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight400,
    lineHeight: '24px',
    color: mixins.color_text_primary
  },
  extraLabel: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '24px',
    color: mixins.color_text_primary
  },
  actionsRow: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    flexWrap: 'wrap',
    marginTop: 32
  },
  cancelButton: {
    borderRadius: 1000,
    border: `2px solid ${mixins.lightTextColor2}`,
    color: mixins.lightTextColor2,
    padding: '16px 24px',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '16px',
    textTransform: 'none',
    backgroundColor: 'transparent',
    maxHeight: 48,
    minWidth: 'unset',

    '& span': {
      minWidth: 104,
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center'
    },

    '&:hover, &.MuiButton-text:hover': {
      backgroundColor: mixins.lightThemeBgColor2,
      background: mixins.lightThemeBgColor2
    },
    '&:disabled': {
      borderColor: mixins.disabledInputBoxColor,
      color: mixins.disabledTextColor
    }
  },
  saveButton: {
    borderRadius: 1000,
    backgroundColor: `${mixins.amethyst} !important`,
    color: `${mixins.chalkWhiteFont} !important`,
    padding: '16px 24px',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '16px',
    textTransform: 'none',
    maxHeight: 48,

    '&:hover': {
      backgroundColor: `${mixins.amethystColorV2} !important`,
      color: `${mixins.chalkWhiteFont} !important`
    },
    '&:disabled, &.Mui-disabled': {
      backgroundColor: `${mixins.amethystDisabledColor} !important`,
      color: mixins.chalkWhiteFont
    }
  },
  modalTitle: {
    margin: 0,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 24,
    fontWeight: mixins.fontWeight500,
    lineHeight: '32px',
    letterSpacing: '-0.48px',
    color: mixins.color_text_primary
  },
  modalSectionTitle: {
    margin: 0,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight600,
    lineHeight: '24px',
    color: mixins.color_text_primary
  },
  modalSectionBody: {
    margin: 0,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight400,
    lineHeight: '24px',
    color: mixins.color_text_primary
  },
  infoModalPaper: {
    borderRadius: 24,
    padding: 48,
    width: 640,
    maxWidth: 640,
    minWidth: 640,
    minHeight: 'auto',
    boxSizing: 'border-box',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(13, 0, 77, 0.04), 0 1px 0.5px rgba(13, 0, 77, 0.1), 0 2px 0.5px rgba(13, 0, 77, 0.15)',

    [mixins['md-sm-maxwidth']]: {
      width: 'calc(100vw - 32px)',
      maxWidth: 'calc(100vw - 32px)',
      minWidth: 0,
      padding: 24,
      borderRadius: 16
    }
  },
  infoModalCloseButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    padding: 4,
    color: mixins.color_text_primary,
    height: 32,
    width: 32,

    '& svg': {
      height: 18,
      width: 18
    }
  },
  infoModalContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24
  },
  infoModalSections: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24
  },
  modalSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4
  }
};

export const classes = createScopedClasses('aet-assignment-settings-form', assignmentSettingsFormStyles);

export const scopedStyles = createScopedStyles(assignmentSettingsFormStyles, classes);

export const AssignmentSettingsFormStylesRoot = styled('div')(scopedStyles);

export const AssignmentSettingsCancelButton = styled(Button)({
  '&&': assignmentSettingsFormStyles.cancelButton
});

/**
 * The gradebook info modal renders through `CustomModal`, which portals to the
 * document body, so none of its slots can be reached by a scope declared here.
 */
export const portalSlotStyles = createPortalSlotStyles(assignmentSettingsFormStyles, classes, [
  'infoModalPaper',
  'infoModalContent',
  'infoModalCloseButton',
  'infoModalSections',
  'modalTitle',
  'modalSection',
  'modalSectionTitle',
  'modalSectionBody'
]);

export default assignmentSettingsFormStyles;
